import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { ArrowLeftIcon, BellIcon } from "@heroicons/react/24/solid";
import { ContentSectionAlt } from "../Layout/ContentSectionAlt";
import { api } from "../api";
import { Button } from "../DesignSystem/Button";
import { RLink } from "../DesignSystem/RLink";
import { MentionTextarea } from "../DesignSystem/MentionTextarea";

export const NewTopicPage = (): JSX.Element => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [topicData, setTopicData] = useState({
    title: "",
    body: "",
    board: "chips" as "chips" | "memes",
    subscribe: true,
  });

  const submit = async () => {
    if (!topicData.title.trim() || !topicData.body.trim()) {
      return;
    }

    setIsSubmitting(true);
    const result = await api.post("/api/forum", {
      title: topicData.title,
      body: topicData.body,
      board: topicData.board,
      subscribe: topicData.subscribe,
    });
    setIsSubmitting(false);

    if (result && result.data) {
      history.push(`/forum/${result.data.id}`);
    }
  };

  return (
    <ContentSectionAlt>
      {/* Header */}
      <div className="mb-6">
        <RLink
          to="/forum"
          className="mb-3 inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to forum
        </RLink>

        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          New Topic
        </h1>
      </div>

      {/* Form card */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-800">
        <div className="space-y-5 p-5">
          {/* Title */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Title
            </label>
            <input
              type="text"
              value={topicData.title}
              onChange={(event) =>
                setTopicData({
                  ...topicData,
                  title: event.currentTarget.value,
                })
              }
              placeholder="What's on your mind?"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-500"
            />
          </div>

          {/* Board selector */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Board
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTopicData({ ...topicData, board: "chips" })}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                  topicData.board === "chips"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                }`}
              >
                Chips
              </button>
              <button
                type="button"
                onClick={() => setTopicData({ ...topicData, board: "memes" })}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                  topicData.board === "memes"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                }`}
              >
                Memes
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              "Chips" for serious discussions, "Memes" for... you know
            </p>
          </div>

          {/* Body */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Body
            </label>
            <MentionTextarea
              value={topicData.body}
              onChange={(body) => setTopicData({ ...topicData, body })}
              placeholder="Write your post here... Use @username to mention someone"
            />
          </div>

          {/* Subscribe checkbox */}
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              checked={topicData.subscribe}
              onChange={(event) =>
                setTopicData({
                  ...topicData,
                  subscribe: event.currentTarget.checked,
                })
              }
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700"
            />
            <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <BellIcon className="h-4 w-4 text-slate-400" />
              Subscribe to notifications
            </div>
          </label>
        </div>

        {/* Footer with submit button */}
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-white/10 dark:bg-slate-700/50">
          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={submit}
              disabled={!topicData.title.trim() || !topicData.body.trim()}
              isLoading={isSubmitting}
              grow={false}
            >
              Post Topic
            </Button>
          </div>
        </div>
      </div>
    </ContentSectionAlt>
  );
};
